<template>
    <div class="tag-component">
        <card title="Tags">
            <template #header-right>
                <div class="d-flex align-items-center justify-content-end">
                    <b-form-select v-model="entityType" :options="filterOptions" @change="onFilterChange"
                        style="width: auto;" />
                </div>
            </template>

            <b-table :items="filteredTags" :fields="fields" small striped hover show-empty
                :empty-text="emptyText" :current-page="pageNumber" :per-page="pageSize"
                :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">
                <template #cell(entityType)="data">
                    <b-badge variant="light">{{ entityTypeLabel(data.item.entityType) }}</b-badge>
                </template>
                <template #cell(behaviors)="data">
                    <b-badge v-for="(b, i) in behaviorSummary(data.item)" :key="i" variant="secondary" class="mr-1">
                        {{ b }}
                    </b-badge>
                </template>
                <template #cell(visibleMarking)="data">
                    <b-badge v-if="data.item.visibleMarking" variant="info">{{ data.item.visibleMarking }}</b-badge>
                </template>
                <template #cell(actions)="data">
                    <b-button size="sm" variant="primary" class="mr-1" @click="openEdit(data.item)">Edit</b-button>
                    <b-button size="sm" variant="danger" @click="remove(data.item)">Delete</b-button>
                </template>
            </b-table>

            <BottomPagerBar v-model="pageNumber" :total-rows="tags.length" :filtered-rows="filteredTags.length"
                :per-page="pageSize" show-page-size-selector show-total item-name="tag"
                @change-page-size="pageSize = $event">
                <template #left-after>
                    <b-button size="sm" variant="primary" @click="openNew">
                        <i class="fas fa-plus" /> New Tag
                    </b-button>
                </template>
            </BottomPagerBar>
        </card>

        <b-modal id="tag-edit-modal" :title="editing.id ? 'Edit Tag' : 'New Tag'" size="lg" @ok="save"
            @cancel="releaseEditLock" @close="releaseEditLock" :ok-disabled="!editing.name">
            <b-form>
                <b-form-row>
                    <b-col md="6">
                        <b-form-group label="Name">
                            <b-form-input v-model="editing.name" placeholder="Tag name" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group label="Entity Type">
                            <b-form-select v-model="editing.entityType" :options="entityTypeOptions"
                                :disabled="!!editing.id" />
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group>
                            <template #label>
                                Rank (higher wins)
                                <HelpPopup>
                                    Global precedence for resolving conflicts. When two attached tags set the
                                    same value behavior (e.g. promise time), the higher rank wins.
                                    Rank is compared across all tag types, so a patient tag can outrank a drug tag,
                                    and ties break by tag id. On/off behaviors and task triggers ignore rank — any
                                    tag that enables them takes effect.
                                </HelpPopup>
                            </template>
                            <b-form-input v-model.number="editing.rank" type="number" />
                        </b-form-group>
                    </b-col>
                </b-form-row>

                <b-form-group>
                    <template #label>
                        Visible Marking (badge/print)
                        <HelpPopup>
                            Optional label shown as a badge on screen and printed on tickets and leaflets for
                            any prescription or order this tag applies to (e.g. REFRIGERATED). Additive — every
                            applicable tag's marking is shown, ordered by rank.
                        </HelpPopup>
                    </template>
                    <b-form-input v-model="editing.visibleMarking" maxlength="50"
                        placeholder="e.g. REFRIGERATED" />
                </b-form-group>

                <hr />
                <h6>Behaviors</h6>

                <!-- Drug behaviors -->
                <template v-if="editing.entityType === TagEntityType.Drug">
                    <b-form-checkbox v-model="editing.shipOvernight">
                        Ship Overnight
                        <HelpPopup>
                            Seeds new orders containing a drug with this tag to Overnight delivery. A default
                            only — staff can change the delivery method before the order ships.
                        </HelpPopup>
                    </b-form-checkbox>
                    <b-form-checkbox v-model="editing.overridePriority">
                        Override Priority
                        <HelpPopup>
                            Flags new orders as priority (prints PRIORITY on the shipping label). Seeded as a
                            default; overridable before the order ships.
                        </HelpPopup>
                    </b-form-checkbox>
                    <b-form-checkbox v-model="editing.disableOneRate">
                        Disable One Rate
                        <HelpPopup>
                            Stops new orders from using FedEx One Rate flat-rate packaging. Seeded as a default;
                            overridable before the order ships.
                        </HelpPopup>
                    </b-form-checkbox>
                    <b-form-checkbox v-model="editing.signatureRequired">
                        Signature Required
                        <HelpPopup>
                            Requires a signature on delivery for new orders. Seeded as a default; overridable
                            before the order ships.
                        </HelpPopup>
                    </b-form-checkbox>
                    <b-form-checkbox v-model="editing.shipToPrescriber">
                        Ship To Prescriber
                        <HelpPopup>
                            Routes new orders direct to the prescriber instead of the patient (layered on top
                            of the existing "Ship To Prescriber" drug category). The prescriber's address and
                            allowed states are validated, and such orders cannot be will-call.
                        </HelpPopup>
                    </b-form-checkbox>
                    <b-form-checkbox v-model="editing.blockRefillRequests">
                        Block Refill Requests Entirely (hard enforcement)
                        <HelpPopup>
                            Hard block — refill requests for this drug are rejected everywhere (manual refill,
                            auto-refill, and the refill-request batch). Not overridable and not subject to rank:
                            any tag with this set blocks, even when a higher-ranked tag does not.
                        </HelpPopup>
                    </b-form-checkbox>
                </template>

                <!-- Shared value behaviors -->
                <b-form-checkbox v-model="editing.defaultUrgency">
                    Default Urgency
                    <HelpPopup>
                        Marks a brand-new prescription as Urgent when its drug, patient, or prescriber carries
                        this tag. Applied only at creation and only if the Rx is not already urgent, so staff
                        can toggle it off afterward.
                    </HelpPopup>
                </b-form-checkbox>
                <b-form-group v-if="showPromiseTime" class="mt-2">
                    <template #label>
                        Promise Time Offset (hours)
                        <HelpPopup>
                            Seeds a new order's promised date to this many hours after it is created. The
                            highest-ranked tag that sets it wins. A default only — overridable before the order
                            ships.
                        </HelpPopup>
                    </template>
                    <b-form-input v-model.number="editing.promiseTimeOffsetHours" type="number" />
                </b-form-group>

                <!-- Task trigger for patient / prescriber -->
                <b-form-group v-if="showTaskTrigger" class="mt-2">
                    <template #label>
                        Task Trigger — Task Template
                        <HelpPopup>
                            After a prescription passes PV1, automatically creates a task from the selected
                            template. Additive — every applicable patient/prescriber tag's template fires
                            (deduplicated); rank never suppresses one.
                        </HelpPopup>
                    </template>
                    <TaskTemplateField v-model="editing.taskTemplateId" />
                </b-form-group>

                <hr />

                <b-form-group label="Notes" class="mt-2">
                    <b-form-textarea v-model="editing.notes" rows="2" />
                </b-form-group>
            </b-form>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator';
import axios from 'axios';
import { Tag, TagEntityType } from '../models/Tag';
import { Lock, LockHandler } from '@/mixins/LockHandler';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import TaskTemplateField from '@/components/Configuration/TaskTemplateField.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';

function emptyTag(entityType: TagEntityType): Partial<Tag> {
    return {
        name: '',
        entityType,
        rank: 0,
        visibleMarking: '',
        defaultUrgency: false,
        shipOvernight: false,
        overridePriority: false,
        disableOneRate: false,
        signatureRequired: false,
        shipToPrescriber: false,
        blockRefillRequests: false,
    };
}

@Component({
    name: "TagComponent",
    components: { TaskTemplateField, HelpPopup, BottomPagerBar },
})
export default class TagComponent extends Mixins(LockHandler) {
    // Expose the enum to the template.
    private TagEntityType = TagEntityType;

    // null => the "All" filter (show every entity type). This is the default.
    private entityType: TagEntityType | null = null;
    private tags: Tag[] = [];
    private editing: Partial<Tag> = emptyTag(TagEntityType.Drug);
    // Edit lock held while the modal is open on an existing tag (LockHandler mixin manages refresh).
    private editingLock: Lock | null = null;

    // Client-side paging + sorting (b-table paginates/sorts the `tags` array locally).
    private pageNumber = 1;
    private pageSize = 25;
    private sortBy = 'rank';
    private sortDesc = true; // highest rank first — the rank that wins conflicts sits at the top.

    private entityTypeOptions = [
        { value: TagEntityType.Drug, text: 'Drug' },
        { value: TagEntityType.Patient, text: 'Patient' },
        { value: TagEntityType.Prescriber, text: 'Prescriber' },
    ];

    private fields = [
        { key: 'name', label: 'Name', tdClass: 'align-middle', sortable: true },
        { key: 'entityType', label: 'Type', tdClass: 'align-middle', sortable: true },
        { key: 'rank', label: 'Rank', tdClass: 'align-middle', sortable: true },
        { key: 'visibleMarking', label: 'Marking', tdClass: 'align-middle', sortable: true },
        { key: 'behaviors', label: 'Behaviors', tdClass: 'align-middle' },
        { key: 'actions', label: '', tdClass: 'align-middle text-right', thClass: 'text-right' },
    ];

    // The page-level filter prepends an "All" (null) choice; the modal's Entity Type selector reuses
    // entityTypeOptions directly since a tag must be scoped to exactly one type.
    get filterOptions() {
        return [{ value: null, text: 'All' }, ...this.entityTypeOptions];
    }

    get emptyText(): string {
        return this.entityType == null
            ? 'No tags defined.'
            : `No ${this.entityTypeLabel(this.entityType)} tags defined.`;
    }

    entityTypeLabel(type: TagEntityType): string {
        return this.entityTypeOptions.find(o => o.value === type)?.text ?? '';
    }

    created() {
        this.fetchTags();
    }

    get showPromiseTime(): boolean {
        return this.editing.entityType === TagEntityType.Drug
            || this.editing.entityType === TagEntityType.Patient;
    }
    get showTaskTrigger(): boolean {
        return this.editing.entityType === TagEntityType.Patient
            || this.editing.entityType === TagEntityType.Prescriber;
    }

    behaviorSummary(tag: Tag): string[] {
        const b: string[] = [];
        if (tag.defaultUrgency) b.push('Urgent');
        // Explicit null checks (not truthiness) so a legitimate 0 offset ("due now") still renders.
        if (tag.promiseTimeOffsetHours != null) b.push(`Promise +${tag.promiseTimeOffsetHours}h`);
        if (tag.shipOvernight) b.push('Overnight');
        if (tag.overridePriority) b.push('Priority');
        if (tag.disableOneRate) b.push('No One-Rate');
        if (tag.signatureRequired) b.push('Signature');
        if (tag.shipToPrescriber) b.push('Ship→Prescriber');
        if (tag.blockRefillRequests) b.push('Block Refills');
        if (tag.taskTemplateId != null) b.push(`Task #${tag.taskTemplateId}`);
        return b;
    }

    fetchTags() {
        // Always load the full set; the entity-type filter is applied client-side (see filteredTags) so the
        // pager can report both the filtered and total counts.
        axios.get(`/Tag/`)
            .then(response => {
                this.tags = response.data;
                this.pageNumber = 1; // reset paging so a refresh never lands on an out-of-range page
            })
            .catch(error => {
                console.warn(error);
                this.$notification(NotificationOptions.error(error));
            });
    }

    // The entity-type dropdown filters the already-loaded set client-side, so the pager keeps the full
    // total while showing the matched count. "All" (null) passes everything through.
    get filteredTags(): Tag[] {
        return this.entityType == null
            ? this.tags
            : this.tags.filter(t => t.entityType === this.entityType);
    }

    onFilterChange() {
        // No refetch needed — just reset to the first page of the newly filtered set.
        this.pageNumber = 1;
    }

    openNew() {
        // The filter may be "All" (null); a tag must be scoped, so default a new tag to Drug.
        this.editing = emptyTag(this.entityType ?? TagEntityType.Drug);
        this.$bvModal.show('tag-edit-modal');
    }

    openEdit(tag: Tag) {
        this.editing = { ...tag };
        if (!tag.id) {
            this.$bvModal.show('tag-edit-modal');
            return;
        }
        // Acquire the edit lock before opening so a concurrent editor is blocked: while this user holds
        // it, the backend SaveTag/DeleteTag reject another user's write with 418. Only open on success.
        this.addLockWithURL(`/Tag/${tag.id}/lock`, 60000)
            .then(lock => {
                this.editingLock = lock;
                this.$bvModal.show('tag-edit-modal');
            })
            .catch(error => {
                if (error.response && error.response.status == 418) {
                    const lockedBy = error.response.data?.lockedBy ?? 'another user';
                    this.$notification(NotificationOptions.error(`This tag is currently being edited by ${lockedBy}.`));
                } else {
                    console.warn(error);
                    this.$notification(NotificationOptions.error(error));
                }
            });
    }

    // v-model.number leaves a cleared input as '' (not null); '' fails to bind to the non-nullable int
    // Rank / decimal? Temp fields on the C# Tag model and 400s the save. Coerce to a number or null.
    private toNumberOrNull(value: unknown): number | null {
        if (value === '' || value === null || value === undefined) return null;
        const n = Number(value);
        return Number.isNaN(n) ? null : n;
    }

    // Public: invoked from the modal @cancel/@close handlers as well as save().
    releaseEditLock() {
        if (this.editingLock?.refreshURL) {
            this.releaseLockAtURL(this.editingLock.refreshURL);
        }
        this.editingLock = null;
    }

    save() {
        const payload = {
            ...this.editing,
            rank: this.toNumberOrNull(this.editing.rank) ?? 0,
            promiseTimeOffsetHours: this.toNumberOrNull(this.editing.promiseTimeOffsetHours),
            taskTemplateId: this.toNumberOrNull(this.editing.taskTemplateId),
        };
        axios.post(`/Tag`, payload)
            .then(() => {
                this.$notification(NotificationOptions.success('Tag saved'));
                this.fetchTags();
            })
            .catch(error => {
                console.warn(error);
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                // Release only after the save round-trips so the lock is held for the write itself
                // (the modal has already closed on OK). Cancel/close/route-change release separately.
                this.releaseEditLock();
            });
    }

    async remove(tag: Tag) {
        if (!tag.id) return;

        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to delete the tag "${tag.name}"? This cannot be undone.`,
            {
                title: 'Delete Tag',
                okTitle: 'Delete',
                okVariant: 'danger',
                centered: true,
            });
        if (!confirmed) return;

        axios.delete(`/Tag/${tag.id}`)
            .then(() => {
                this.$notification(NotificationOptions.success('Tag deleted'));
                this.fetchTags();
            })
            .catch(error => {
                console.warn(error);
                this.$notification(NotificationOptions.error(error));
            });
    }
}
</script>

<style>
/* BootstrapVue transports the modal to <body>, so component-scoped styles don't reach it — target the
   modal by its id globally instead. Bootstrap's `.custom-select` forces an explicit height
   (calc(1.5em + 0.75rem + 2px)) and line-height: 1.5, so the native Entity Type <select> renders taller
   than the now-ui `.form-control` inputs (height: auto; line-height: normal; same 14px font + 6px
   padding). Match those two properties so the three top-row fields align. */
#tag-edit-modal .custom-select {
    height: auto;
    line-height: normal;
}
</style>
